import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Star, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/reviews`);
      if (response.ok) setReviews(await response.json());
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'Anonym', rating, comment: comment.trim() })
      });
      if (response.ok) {
        toast.success('Thanks for your review!');
        setName('');
        setRating(5);
        setComment('');
        fetchReviews();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="reviews-page">
      <Navbar />

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-babyblue-900 mb-4">Success Stories</h1>
            <p className="text-lg text-babyblue-600">See what our learners are saying about Germanatic</p>
          </div>

          {/* Public review form */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8 mb-12" data-testid="review-form-card">
            <h2 className="font-heading text-2xl font-semibold text-babyblue-900 mb-2">Share your story</h2>
            <p className="text-babyblue-600 text-sm mb-6">Your name is optional — leave it empty to post as <em>Anonym</em>.</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-babyblue-700 mb-2">Name <span className="text-babyblue-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  className="w-full px-4 py-3 border-2 border-babyblue-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                  placeholder="Anonym"
                  data-testid="review-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-babyblue-700 mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="focus:outline-none"
                      data-testid={`review-star-${star}`}
                    >
                      <Star className={`w-8 h-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-babyblue-300'}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-babyblue-700 mb-2">Your review</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                  rows="4"
                  maxLength={2000}
                  className="w-full px-4 py-3 border-2 border-babyblue-200 rounded-lg focus:border-primary focus:outline-none resize-none transition-colors"
                  placeholder="Share your experience with Germanatic..."
                  data-testid="review-comment-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !comment.trim()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="review-submit-button"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </form>
          </div>

          {/* Reviews list */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner"></div></div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-babyblue-200">
              <p className="text-babyblue-600">No reviews yet. Be the first to leave one!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.review_id} className="bg-white rounded-2xl shadow-sm border border-babyblue-200 p-6" data-testid={`review-${review.review_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-babyblue-900">{review.user_name}</h3>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-babyblue-300'}`} />
                        ))}
                      </div>
                    </div>
                    <span className="text-sm text-babyblue-500">
                      {new Date(review.created_at).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <p className="text-babyblue-700 leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReviewsPage;
